import { QuizDataSchema, type QuizDefinition } from "@/schema/quizSchema";
import { validateQuiz } from "@/validation/validateQuiz";

/** One result for the whole quiz-validity question (structural + behavioural). */
export type QuizValidity = { ok: true } | { ok: false; errors: string[] };

/**
 * The single quiz-validity seam (ADR-0007): compose the Zod *data* parse
 * (structural shape) with the {@link validateQuiz} *walker* (branching
 * terminates, reachable questions, classifier returns only declared personas)
 * into one `{ ok } | { ok, errors }`.
 *
 * Callers (the QuizWidget boot parse and the CI `validate-quiz` script) ask this
 * one function instead of re-doing the strip-functions + safeParse + walker dance.
 *
 * `behavioural` gates the {@link validateQuiz} graph walk. It defaults to ON (the CI
 * `validate-quiz` script and dev/test callers rely on that default) but MUST be turned OFF for
 * production widget boot: the walk is an authoring/CI check, not a runtime check, and running it
 * synchronously on every mount blocks the main thread for up to ~2s on real quizzes (see
 * validateQuiz.ts). Production boot should only ever run the structural Zod parse.
 */
export function validateQuizDefinition(
  quiz: QuizDefinition,
  { behavioural = true }: { behavioural?: boolean } = {},
): QuizValidity {
  const errors: string[] = [];

  // Structural: the serializable data (functions stripped — they are validated
  // behaviourally below, not by Zod).
  const { branching, classifier, renderResult, renderBuilding, ...data } = quiz;
  void branching;
  void classifier;
  void renderResult;
  void renderBuilding;
  const parsed = QuizDataSchema.safeParse(data);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      const path = issue.path.join(".");
      errors.push(path ? `${path}: ${issue.message}` : issue.message);
    }
  }

  // Behavioural: the walker is independent of the Zod parse and self-guards
  // against an empty questions array. Opt-in (defaults ON) — see the `behavioural`
  // doc comment above for why production boot must turn it off.
  if (behavioural) errors.push(...validateQuiz(quiz));

  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}
